import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * `yp_folders` row shaped the same as the dashboard/employee `folders`
 * group's own-client `FolderRow` — this group is the admin cross-client
 * variant: any client's folder tree, addressed by an explicit `client_id`,
 * for the add-client wizard's "create the client's protected folder" step
 * and any future admin folder management. `depth`/`materialized_path` are
 * computed by the `set_folder_path` DB trigger, never supplied by the
 * caller.
 */
export const AdminFolderRow = S.Struct({
    id: S.UUID,
    client_id: S.NullOr(S.UUID),
    name: S.String,
    name_t_en: S.NullOr(S.String),
    parent: S.NullOr(S.UUID),
    depth: S.Int,
    materialized_path: S.NullOr(S.String),
});
export const ListAdminFoldersParams = S.Struct({
    client_id: S.UUID,
    parent: S.optional(S.UUID),
});
/**
 * Flat listing of one client's folders directly under `parent` (root when
 * omitted) — no recursive tree walk; the add-client wizard only ever checks
 * "does this client already have a folder under X" before creating one.
 */
export const ListAdminFolders = HttpApiEndpoint.get("listAdminFolders", "/admin/folders")
    .setUrlParams(ListAdminFoldersParams)
    .addSuccess(S.Struct({ items: S.Array(AdminFolderRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const CreateAdminFolderPayload = S.Struct({
    /** Omitted means the global internal document tree. */
    client_id: S.optional(S.UUID),
    name: S.NonEmptyString,
    name_t_en: S.optional(S.NullOr(S.String)),
    parent: S.optional(S.UUID),
});
export const CreateAdminFolder = HttpApiEndpoint.post("createAdminFolder", "/admin/folders/create")
    .setPayload(CreateAdminFolderPayload)
    .addSuccess(S.Struct({ data: AdminFolderRow }))
    .addError(Forbidden, { status: 403 });
export const AdminFolderNotFound = S.Struct({
    _tag: S.Literal("AdminFolderNotFound"),
});
export const DeleteAdminFolderPayload = S.Struct({
    id: S.UUID,
    /** Omitted means the global internal document tree. */
    client_id: S.optional(S.UUID),
});
/**
 * Deletes cascade onto subfolders and their files
 * (`yp_folders_parent_foreign` / the files→folder FK are both
 * `ON DELETE CASCADE` — see `20251030A-fix-cascade-delete-folders-files`).
 * This intent does not re-guard that cascade; it is the same behaviour the
 * legacy `ItemsService.deleteOne` gave any caller with delete rights.
 */
export const DeleteAdminFolder = HttpApiEndpoint.post("deleteAdminFolder", "/admin/folders/delete")
    .setPayload(DeleteAdminFolderPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(AdminFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const GetAdminFolderParams = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
/** Single-folder read scoped the same way every other admin-folders read is — id+client_id mismatch is `AdminFolderNotFound`, never a cross-client leak. */
export const GetAdminFolder = HttpApiEndpoint.get("getAdminFolder", "/admin/folders/get")
    .setUrlParams(GetAdminFolderParams)
    .addSuccess(S.Struct({ data: AdminFolderRow }))
    .addError(AdminFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const UpdateAdminFolderPayload = S.Struct({
    id: S.UUID,
    /** Omitted means the global internal document tree. */
    client_id: S.optional(S.UUID),
    name: S.optional(S.NonEmptyString),
    name_t_en: S.optional(S.NullOr(S.String)),
    parent: S.optional(S.NullOr(S.UUID)),
});
/**
 * `parent` reparenting relies on the same `set_folder_path` DB trigger
 * `createAdminFolder` does for its initial `depth`/`materialized_path` — this
 * intent never computes those columns itself.
 */
export const UpdateAdminFolder = HttpApiEndpoint.post("updateAdminFolder", "/admin/folders/update")
    .setPayload(UpdateAdminFolderPayload)
    .addSuccess(S.Struct({ data: AdminFolderRow }))
    .addError(AdminFolderNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminFoldersGroup = HttpApiGroup.make("admin-folders")
    .add(ListAdminFolders)
    .add(GetAdminFolder)
    .add(CreateAdminFolder)
    .add(UpdateAdminFolder)
    .add(DeleteAdminFolder)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-folders.js.map